import { TreeNodeData } from "@asup/tree-of-nodes";
import { eAccessRights, eHierarchy } from "functions/api/enums";
import { ReportTlfCopySource } from "functions/api/hierarchy/copyTlfsToHierarchy";
import { HierarchyChildReadDto } from "functions/api/hierarchy/HierarchyChildReadDto";
import { HierarchyReadDto } from "functions/api/hierarchy/HierarchyReadDto";
import { TlfItemIndexReadDto } from "functions/api/tlfItem/TlfItemIndexReadDto";

/**
 * A source selection that can independently include its Master and named variants.
 */
export interface HierarchyTlfSelection extends ReportTlfCopySource {
  azsolId: string;
  domain: string;
}

export const ADD_HIERARCHY_NODES = "ADD_HIERARCHY_NODES";
export const ADD_SELECTION = "ADD_SELECTION";
export const REMOVE_SELECTION = "REMOVE_SELECTION";
export const SET_CORPORATE_HIERARCHY = "SET_CORPORATE_HIERARCHY";
export const SET_CURRENT_ACCESS_RIGHTS = "SET_CURRENT_ACCESS_RIGHTS";
export const SET_CURRENT_HIERARCHY = "SET_CURRENT_HIERARCHY";
export const SET_NEW_REPORT_DETAILS = "SET_NEW_REPORT_DETAILS";
export const SET_NEW_TLF_ITEM_DETAILS = "SET_NEW_TLF_ITEM_DETAILS";
export const SET_NEW_TLF_ITEM_DETAILS_VALID = "SET_NEW_TLF_ITEM_DETAILS_VALID";
export const SET_SELECTIONS = "SET_SELECTIONS";
export const UPSERT_HIERARCHY_TLF_ITEM_INDEX = "UPSERT_HIERARCHY_TLF_ITEM_INDEX";

export type HierarchyNodeData =
  (HierarchyReadDto & { url: string }) | (HierarchyChildReadDto & { url: string });

interface NewTlfItemDetails {
  additionalInformation?: string;
  azsolId?: string;
  sapReference?: string;
  selectedDomain?: string;
  tocNumber?: string;
  version?: number;
}

export interface IHierarchyBrowserContext {
  canSelect: boolean;
  currentAccessRights: eAccessRights[] | null;
  currentHierarchy: string;
  currentHierarchyType: eHierarchy | null;
  hierarchyTlfItemIndex: { hierarchyId: string; tlfItemIndex: TlfItemIndexReadDto[] }[];
  id: string;
  newReportDescription?: string;
  newReportName?: string;
  newReportType?: string;
  newReportValid?: boolean;
  newTlfItemDetails?: NewTlfItemDetails;
  newTlfItemDetailsValid?: boolean;
  root: string;
  selections: HierarchyTlfSelection[];
  showTableLinks: boolean;
  treeList: TreeNodeData<HierarchyNodeData>[];
}

export type HierarchyBrowserOperation =
  | typeof ADD_HIERARCHY_NODES
  | typeof ADD_SELECTION
  | typeof REMOVE_SELECTION
  | typeof SET_CORPORATE_HIERARCHY
  | typeof SET_CURRENT_ACCESS_RIGHTS
  | typeof SET_CURRENT_HIERARCHY
  | typeof SET_NEW_REPORT_DETAILS
  | typeof SET_NEW_TLF_ITEM_DETAILS
  | typeof SET_NEW_TLF_ITEM_DETAILS_VALID
  | typeof SET_SELECTIONS
  | typeof UPSERT_HIERARCHY_TLF_ITEM_INDEX;

export interface IHierarchyBrowserActionBase {
  operation: HierarchyBrowserOperation;
}

export interface IHierarchyBrowserActionAddHierarchyNodes extends IHierarchyBrowserActionBase {
  operation: typeof ADD_HIERARCHY_NODES;
  treeListData: TreeNodeData<HierarchyNodeData>[];
}

export interface IHierarchyBrowserActionAddSelection extends IHierarchyBrowserActionBase {
  operation: typeof ADD_SELECTION;
  selection: HierarchyTlfSelection;
}

export interface IHierarchyBrowserActionRemoveSelection extends IHierarchyBrowserActionBase {
  operation: typeof REMOVE_SELECTION;
  selection: HierarchyTlfSelection;
}

export interface IHierarchyBrowserActionSetCorporateHierarchy extends IHierarchyBrowserActionBase {
  hierarchyNode: HierarchyReadDto;
  operation: typeof SET_CORPORATE_HIERARCHY;
}

export interface IHierarchyBrowserActionSetCurrentAccessRights extends IHierarchyBrowserActionBase {
  accessRights: eAccessRights[] | null;
  operation: typeof SET_CURRENT_ACCESS_RIGHTS;
}

export interface IHierarchyBrowserActionSetCurrentHierarchy extends IHierarchyBrowserActionBase {
  nodeId: string;
  operation: typeof SET_CURRENT_HIERARCHY;
}

export interface IHierarchyBrowserActionSetNewReportName extends IHierarchyBrowserActionBase {
  newReportDescription: string;
  newReportName: string;
  newReportType: string;
  newReportValid: boolean;
  operation: typeof SET_NEW_REPORT_DETAILS;
}

export interface IHierarchyBrowserActionSetNewTlfItemDetails extends IHierarchyBrowserActionBase {
  operation: typeof SET_NEW_TLF_ITEM_DETAILS;
  tlfItemDetails: NewTlfItemDetails;
}

export interface IHierarchyBrowserActionSetNewTlfItemDetailsValid extends IHierarchyBrowserActionBase {
  operation: typeof SET_NEW_TLF_ITEM_DETAILS_VALID;
  tlfItemDetailsValid: boolean;
}

export interface IHierarchyBrowserActionSetSelections extends IHierarchyBrowserActionBase {
  operation: typeof SET_SELECTIONS;
  selections: HierarchyTlfSelection[];
}

export interface IHierarchyBrowserActionUpsertHierarchyTlfItemIndex extends IHierarchyBrowserActionBase {
  nodeId: string;
  operation: typeof UPSERT_HIERARCHY_TLF_ITEM_INDEX;
  tlfItemIndex: TlfItemIndexReadDto[];
}

export type IHierarchyBrowserAction =
  | IHierarchyBrowserActionAddHierarchyNodes
  | IHierarchyBrowserActionAddSelection
  | IHierarchyBrowserActionRemoveSelection
  | IHierarchyBrowserActionSetCorporateHierarchy
  | IHierarchyBrowserActionSetCurrentAccessRights
  | IHierarchyBrowserActionSetCurrentHierarchy
  | IHierarchyBrowserActionSetNewReportName
  | IHierarchyBrowserActionSetNewTlfItemDetails
  | IHierarchyBrowserActionSetNewTlfItemDetailsValid
  | IHierarchyBrowserActionSetSelections
  | IHierarchyBrowserActionUpsertHierarchyTlfItemIndex;
